import { useState } from 'react';
import { motion } from 'framer-motion';
import { VideoSlideData, Theme, slideTypographyStyle } from '../slideTypes';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerInput, DrawerNote } from '../shared/SlideShared';
import { getVideoEmbedUrl } from '../../../../lib/videoEmbed';
import { springs } from '../../../ui/slide-animations';

export function VideoSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: VideoSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: VideoSlideData) => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const embedUrl = getVideoEmbedUrl(data.url);
  const isEmbedUrl = !!embedUrl;

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Video Settings"
    >
      <DrawerSection label="Video URL">
        <DrawerInput
          type="url"
          value={data.url}
          onChange={(e) => onUpdate({ ...data, url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
        />
      </DrawerSection>
      <DrawerSection label="Caption (optional)">
        <DrawerInput
          type="text"
          value={data.caption || ''}
          onChange={(e) => onUpdate({ ...data, caption: e.target.value })}
          placeholder="Caption..."
        />
      </DrawerSection>
      <DrawerNote title="Supported formats">
        <ul style={{ listStyle: 'disc', paddingLeft: 16, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <li>YouTube (any URL format)</li>
          <li>Vimeo</li>
          <li>Loom</li>
          <li>Mux (player.mux.com or stream.mux.com)</li>
          <li>Direct .mp4, .webm, or .ogg</li>
        </ul>
      </DrawerNote>
    </SlideSettingsDrawer>
  );

  if (!data.url) {
    return (
      <div className="relative h-full group" style={{ backgroundColor: theme.colors.surface }}>
        <div className="h-full flex flex-col items-center justify-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent, #3b82f6) 10%, transparent)' }}
          >
            <svg className="w-8 h-8" style={{ color: 'var(--accent, #3b82f6)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center">
            <p className="font-medium text-sm mb-0.5" style={{ color: theme.colors.text }}>Video</p>
            <p className="text-xs" style={{ color: theme.colors.textMuted }}>No video URL configured</p>
          </div>
          {isEditing && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--accent, #2563eb)' }}
            >
              Add Video
            </button>
          )}
        </div>
        {settingsDrawer}
      </div>
    );
  }

  return (
    <div className="relative h-full group">
      <motion.div
        className="w-full h-full"
        initial={!isEditing ? { opacity: 0, scale: 1.05 } : false}
        animate={{ opacity: 1, scale: 1 }}
        transition={springs.smooth as object}
      >
        {isEmbedUrl ? (
          <iframe
            src={embedUrl!}
            title={data.caption || 'Embedded video'}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <video src={data.url} className="w-full h-full object-cover" controls />
        )}
      </motion.div>
      {data.caption && !isEmbedUrl && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 px-8 py-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none"
          initial={!isEditing ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...(springs.gentle as object), delay: 0.3 }}
        >
          <p
            className="text-white"
            style={{
              ...slideTypographyStyle(theme, 'caption', theme.fonts.body),
              color: 'white',
            }}
          >
            {data.caption}
          </p>
        </motion.div>
      )}
      {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
      {settingsDrawer}
    </div>
  );
}
